import {Map, Set} from 'immutable';
import {
  mergeDefaultStyleBlocksWithMembership,
  importStyleBlocks,
  buildParentIndex,
  buildOrderList,
  buildAffectsMap,
  buildCssPropertyMap,
} from '@packages/shared/LegacyStyleSystem/utils/StyleBlockUtils';
import type {RawStyleBlockServerType as ServerStyleBlock} from '@packages/systems/core/utils/StyleTypes';
import StyleBlockState from '@packages/shared/LegacyStyleSystem/states/StyleBlockState';

export type ImportRemoteBlocksOptions = {
  rawBlocks: Array<ServerStyleBlock>;
  migrations?: Record<any, any>;
  validPageIds?: Array<string>;
  mergeDefaults?: boolean;
  cleanChildIds?: boolean;
  assetState?: any;
  importErrors?: Array<Record<any, any>>;
  styleBlockIds?: Set<string>;
  baseStyleBlockIds?: Set<string>;
  branchId?: string | null;
};

import type {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';
import type {StyleBlock} from '@packages/systems/style-legacy/records';

/**
 * Injects server-side style block data.
 * @param  {Object[]}   options.rawBlocks     The list of server-side styles.
 * @param  {Object}     options.migrations    The map of server-side migration flags.
 * @param  {String[]}   options.validPageIds  The list of valid pageIds to filter server-side .affects maps.
 * @param  {Boolean}    options.mergeDefaults If true, default "fake" tag selectors will be merged in.
 * @param  {AssetState} options.assetState    The Flux AssetStore state of image assets for the site being getStyed.
 * @param  {Object[]}   options.importErrors  A reference to an array to which import errors should be logged (for debugging).
 * @param  {Set<string>}        options.styleBlockIds  The set of style block IDs that belong to a page/site or page branch
 * @param  {Set<string>}        options.baseStyleBlockIds  The set of style block IDs that are unique to a page/site, separate from a page branch
 */
export function importRemoteBlocks(
  state: StyleBlockStateType = new StyleBlockState(),
  // @ts-expect-error - TS2741 - Property 'rawBlocks' is missing in type '{}' but required in type 'ImportRemoteBlocksOptions'.
  {
    rawBlocks = [],
    migrations = {},
    validPageIds = [],
    mergeDefaults = false,
    cleanChildIds = true,
    assetState = null,
    importErrors = [],
    styleBlockIds = Set(),
    baseStyleBlockIds = Set(),
    branchId = null,
  }: ImportRemoteBlocksOptions = {}
): StyleBlockStateType {
  let mergedRawBlocks = rawBlocks;
  if (mergeDefaults) {
    // See mergeDefaultStyleBlocksWithMembership for the invariant.
    ({
      rawBlocks: mergedRawBlocks,
      styleBlockIds,
      baseStyleBlockIds,
    } = mergeDefaultStyleBlocksWithMembership(
      rawBlocks,
      styleBlockIds,
      baseStyleBlockIds
    ));
  }

  const styleBlocks = importStyleBlocks(mergedRawBlocks, {
    cleanChildIds,
    assetState,
    importErrors,
  });
  const affectsMap = buildAffectsMap(mergedRawBlocks, {
    validPageIds,
  });
  const parentIndex = buildParentIndex(styleBlocks);
  const orderList = buildOrderList(styleBlocks, parentIndex);
  const cssPropertyMap = buildCssPropertyMap(styleBlocks);

  return state
    .resetAddedRule()
    .set(
      'styleBlocks',
      Map(
        styleBlocks.map((block): [string, StyleBlock] => [
          block.get('_id'),
          block,
        ])
      )
    )
    .set('migrations', Map(migrations))
    .set('affectsMap', affectsMap)
    .set('parentIndex', parentIndex)
    .set('globalOrder', orderList)
    .set('cssPropertyMap', cssPropertyMap)
    .set('styleBlockIds', styleBlockIds)
    .set('baseStyleBlockIds', baseStyleBlockIds)
    .set('branchId', branchId ?? null);
}
